import { createEvent, createEffect, sample, createStore } from "effector";
import { ModelContract, ModuleDescription } from "../module-description";
import { useRouter } from "next/navigation";

type IUseRouter = ReturnType<typeof useRouter> | null;

export const routerInit = createEvent<IUseRouter>();
export const routerPush = createEvent<string>();
export const routerReplace = createEvent<string>();
export const routerBack = createEvent();

export const $router = createStore<IUseRouter>(null).on(routerInit, (_, r) => r);

const $history = createStore<string[]>([])
    .on(routerPush, (s, url) => {
        const prev = typeof window !== "undefined" ? window.location.pathname : "";
        const nextStack = [...s];
        if (prev && nextStack[nextStack.length - 1] !== prev) nextStack.push(prev);
        nextStack.push(url);
        return nextStack;
    })
    .on(routerReplace, (s, url) => (s.length ? [...s.slice(0, -1), url] : [url]))
    .on(routerBack, (s) => (s.length ? s.slice(0, -1) : s));

export const $previousUrl = $history.map((l) => (l.length > 1 ? l[l.length - 2] : null));

const pushFx = createEffect(({ url, router }: { url: string; router: IUseRouter }) => {
    router?.push(url);
});
const replaceFx = createEffect(({ url, router }: { url: string; router: IUseRouter }) => {
    router?.replace(url);
});
const backFx = createEffect((router: IUseRouter) => {
    router?.back();
});

sample({
    clock: routerPush,
    source: $router,
    filter: Boolean,
    fn: (router, url) => ({ url, router }),
    target: pushFx
});
sample({
    clock: routerReplace,
    source: $router,
    filter: Boolean,
    fn: (router, url) => ({ url, router }),
    target: replaceFx
});
sample({
    clock: routerBack,
    source: $router,
    fn: (router) => router,
    target: backFx
});

const contract = new ModelContract({ $previousUrl }, { routerPush, routerReplace, routerBack }, {}, {});

export type IRouterContract = typeof contract;

export class RouterModule extends ModuleDescription<IRouterContract> {
    getDefaultContract() {
        return contract;
    }
}
